import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  SimpleGrid,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Circle,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { Tooltip } from "@chakra-ui/react";
import PageHeader from "../components/PageHeader";
import { inviteSubject } from "../utils/inviteSubject";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getPool, updatePool, getPlPayouts, savePlPayouts, sendPlInvites, sendPlRecapTest, togglePlMemberPaid, togglePlCoAdmin, removePlMember } from "../api/pools";
import { usePlSeason } from "../hooks/usePlSeason";
import { generateInviteLink } from "../api/invite";
import PayoutTotalLine from "../components/PayoutTotalLine";
import { payoutTotalError } from "../utils/payoutTotals";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE    = "#00007f";

export default function PLPoolSetup() {
  const { poolId }  = useParams();
  const navigate    = useNavigate();
  const location    = useLocation();
  const { user, logout } = useAuth();
  const toast = useToast();
  const { seasonId: _hookSeasonId } = usePlSeason();
  // Prefer season ID passed via navigation state (needed during setup window when new pools
  // are in the upcoming season but usePlSeason still returns the ended season)
  const SEASON_ID = location.state?.seasonId ?? _hookSeasonId;

  const username = user?.username || "Player";

  const [pool,        setPool]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [loadError,   setLoadError]   = useState("");

  const [name,          setName]          = useState("");
  const [poolDepth,     setPoolDepth]     = useState("");
  const [isPrivate,     setIsPrivate]     = useState(false);
  const [weeklyRecap,   setWeeklyRecap]   = useState(true);
  const [pickReminders, setPickReminders] = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [saveError,     setSaveError]     = useState("");
  const [sendingRecap,  setSendingRecap]  = useState(false);

  const [payouts,       setPayouts]       = useState([]);
  const [payoutError,   setPayoutError]   = useState("");
  const [savingPayouts, setSavingPayouts] = useState(false);

  const [emailInput,    setEmailInput]    = useState("");
  const [emails,        setEmails]        = useState([]);
  const [sendMode,      setSendMode]      = useState("invite"); // "invite" | "message"
  const [message,       setMessage]       = useState("");
  const [sending,       setSending]       = useState(false);
  const [inviteLink,    setInviteLink]    = useState("");
  const [linkLoading,   setLinkLoading]   = useState(false);
  const [invitesClosed, setInvitesClosed] = useState(false);

  useEffect(() => {
    if (SEASON_ID) loadPool();
  }, [poolId, SEASON_ID]); // eslint-disable-line react-hooks/exhaustive-deps

  function buildInviteMessage(poolName, inviterName, link, depth) {
    // Stated up front so an invitee knows the cost before joining.
    // Omitted entirely when the pool has no buy-in.
    const amount = Number(depth);
    const buyIn =
      depth && !Number.isNaN(amount) && amount > 0
        ? ` The buy-in for this pool is currently $${
            Number.isInteger(amount) ? amount : amount.toFixed(2)
          }.`
        : "";
    return (
      `Hi!\n\nYou've been invited to join "${poolName}" on The Deep End — a Premier League Pick 'Em pool — by ${inviterName}.${buyIn}\n\n` +
      `Click the link below to join directly:\n${link}\n\n` +
      `Good luck!\n— ${inviterName}`
    );
  }

  async function loadPool() {
    setLoading(true);
    setLoadError("");
    try {
      const [data, payoutData] = await Promise.all([
        getPool(SEASON_ID, poolId),
        getPlPayouts(SEASON_ID, poolId),
      ]);
      setPool(data);
      setName(data.name || "");
      setPoolDepth(data.pool_depth != null ? String(data.pool_depth) : "");
      setIsPrivate(data.is_private ?? false);
      setWeeklyRecap(data.weekly_recap_enabled ?? true);
      setPickReminders(data.pick_reminders_enabled ?? true);
      setPayouts(payoutData.length > 0 ? payoutData : [
        { label: "1st Place", amount: "" },
        { label: "2nd Place", amount: "" },
        { label: "3rd Place", amount: "" },
      ]);
      try {
        const { link } = await generateInviteLink({ game: "pl", poolId: Number(poolId), seasonId: SEASON_ID });
        setInviteLink(link);
        setMessage(buildInviteMessage(data.name, username, link, data.pool_depth));
      } catch {
        setInvitesClosed(true);
        setSendMode("message");
      }
    } catch (err) {
      setLoadError(err?.response?.data?.detail || "Failed to load pool.");
    } finally {
      setLoading(false);
    }
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  function addEmail() {
    // Split on commas, semicolons and whitespace so a pasted list becomes
    // separate tags. Typing fires this per address via the key handler, but a
    // paste never does, and the whole string would otherwise be accepted as
    // one address because it happens to contain an "@".
    const parts = emailInput
      .split(/[\s,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e && e.includes("@"));
    if (!parts.length) return;
    setEmails((prev) => {
      const next = [...prev];
      parts.forEach((e) => { if (!next.includes(e)) next.push(e); });
      return next;
    });
    setEmailInput("");
  }

  function handleEmailKeyDown(e) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail();
    }
  }

  function removeEmail(addr) {
    setEmails((prev) => prev.filter((e) => e !== addr));
  }

  function addAllMembers() {
    const memberEmails = (pool?.members || [])
      .map((m) => m.email)
      .filter((e) => e && e.includes("@") && !emails.includes(e));
    if (memberEmails.length > 0) setEmails((prev) => [...prev, ...memberEmails]);
  }

  async function handleSave() {
    setSaveError("");
    if (!name.trim()) { setSaveError("Pool name is required."); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), is_private: isPrivate };
      if (poolDepth) body.pool_depth = parseInt(poolDepth, 10);
      const updated = await updatePool(SEASON_ID, poolId, body);
      setPool(updated);
      toast({ title: "Settings saved.", status: "success", duration: 3000, isClosable: true });
    } catch (err) {
      setSaveError(err?.response?.data?.detail || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSavePayouts() {
    setPayoutError("");
    const rows = payouts.map((p) => ({ label: p.label.trim(), amount: parseFloat(p.amount) }));
    if (rows.some((r) => !r.label || isNaN(r.amount) || r.amount <= 0)) {
      setPayoutError("All rows must have a label and a positive amount.");
      return;
    }
    // Allow under, block over — see utils/payoutTotals.js. The server
    // enforces the same rule; this is it said early.
    const overPot = payoutTotalError(rows.map((r) => r.amount), pool?.total_pot);
    if (overPot) {
      setPayoutError(overPot);
      return;
    }
    setSavingPayouts(true);
    try {
      const saved = await savePlPayouts(SEASON_ID, poolId, rows);
      setPayouts(saved);
      toast({ title: "Payouts saved.", status: "success", duration: 3000, isClosable: true });
    } catch (err) {
      setPayoutError(err?.response?.data?.detail || "Failed to save payouts.");
    } finally {
      setSavingPayouts(false);
    }
  }

  async function handleTogglePaid(userId, newPaid) {
    try {
      await togglePlMemberPaid(SEASON_ID, poolId, userId, newPaid);
      setPool((prev) => ({
        ...prev,
        members: prev.members.map((m) =>
          m.user_id === userId ? { ...m, paid: newPaid } : m
        ),
      }));
    } catch (err) {
      toast({
        title: err?.response?.data?.detail || "Failed to update payment status.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  }

  async function switchToInvite() {
    setSendMode("invite");
    if (inviteLink) {
      setMessage(buildInviteMessage(pool?.name || "", username, inviteLink, pool?.pool_depth));
      return;
    }
    setLinkLoading(true);
    try {
      const { link } = await generateInviteLink({ game: "pl", poolId: Number(poolId), seasonId: SEASON_ID });
      setInviteLink(link);
      setMessage(buildInviteMessage(pool?.name || "", username, link, pool?.pool_depth));
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "Failed to generate invite link.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLinkLoading(false);
    }
  }

  function switchToMessage() {
    setSendMode("message");
    setMessage("");
  }

  async function handleCopyLink() {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    toast({ title: "Link copied!", status: "success", duration: 2000, isClosable: true });
  }

  async function handleSend() {
    if (emails.length === 0) {
      toast({ title: "Add at least one email.", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    if (!message.trim()) {
      toast({ title: "Message cannot be empty.", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    setSending(true);
    try {
      const subject = inviteSubject(sendMode, pool?.name);
      await sendPlInvites(SEASON_ID, poolId, emails, message, subject, sendMode);
      setEmails([]);
      if (sendMode === "message") setMessage("");
      toast({ title: sendMode === "invite" ? "Invites sent!" : "Message sent!", status: "success", duration: 4000, isClosable: true });
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "Failed to send.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setSending(false);
    }
  }

  const isOwner = pool && user && pool.owner_user_id === user.id;
  const isAdmin = isOwner || !!pool?.current_user_is_co_admin;

  async function handleToggleCoAdmin(userId, newVal) {
    try {
      await togglePlCoAdmin(SEASON_ID, poolId, userId, newVal);
      setPool((prev) => ({
        ...prev,
        members: prev.members.map((m) =>
          m.user_id === userId ? { ...m, is_co_admin: newVal } : m
        ),
      }));
    } catch {
      toast({ title: "Failed to update co-admin status", status: "error", duration: 3000, isClosable: true });
    }
  }

  async function handleRemoveMember(userId) {
    // Irreversible and one stray click from the paid/co-admin checkboxes, so
    // confirm by name first.
    const member = (pool?.members || []).find((m) => m.user_id === userId);
    const who = member?.username || member?.email || "this member";
    if (!window.confirm(`Remove ${who} from this pool? Their picks will no longer count.`)) {
      return;
    }

    try {
      await removePlMember(SEASON_ID, poolId, userId);
      setPool((prev) => ({ ...prev, members: prev.members.filter((m) => m.user_id !== userId) }));
      toast({ title: "Member removed.", status: "info", duration: 3000, isClosable: true });
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "Failed to remove member.", status: "error", duration: 4000, isClosable: true });
    }
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">
      <PageHeader
        title="PL PICK 'EM POOL SETUP"
        onBack={() => navigate(`/premier-league/current/${poolId}`)}
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="container.xl" py={10}>
        {loading ? (
          <Flex justify="center" align="center" minH="300px"><Spinner size="xl" /></Flex>
        ) : loadError ? (
          <Alert status="error" borderRadius="md"><AlertIcon />{loadError}</Alert>
        ) : (
          <VStack align="stretch" spacing={6}>

            <Heading size="lg">{pool.name}</Heading>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} alignItems="stretch">

              {/* Pool Settings */}
              <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={8} h="100%">
                <VStack align="stretch" spacing={5}>
                  <Heading size="md" color={TOP_BAR_BLUE}>Pool Settings</Heading>

                  {saveError && <Alert status="error" borderRadius="md"><AlertIcon />{saveError}</Alert>}

                  <SimpleGrid columns={2} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Pool Name</FormLabel>
                      <Input value={name} onChange={(e) => setName(e.target.value)} isDisabled={!isOwner} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Buy-in ($)</FormLabel>
                      <Input
                        type="number"
                        min={1}
                        value={poolDepth}
                        onChange={(e) => setPoolDepth(e.target.value)}
                        placeholder="None"
                        isDisabled={!isOwner}
                      />
                    </FormControl>
                  </SimpleGrid>

                  <Divider />

                  <HStack justify="space-between">
                    <Box>
                      <FormLabel fontSize="sm" mb={0}>Private Pool</FormLabel>
                      <Text fontSize="xs" color="gray.500">Invite only — visible but not joinable</Text>
                    </Box>
                    <Switch
                      isChecked={isPrivate}
                      onChange={(e) => setIsPrivate(e.target.checked)}
                      colorScheme="blue"
                      isDisabled={!isOwner}
                    />
                  </HStack>

                  <HStack justify="space-between">
                    <Box>
                      <FormLabel fontSize="sm" mb={0}>Weekly Recap Emails</FormLabel>
                      <Text fontSize="xs" color="gray.500">Send automated matchweek recaps to all members</Text>
                    </Box>
                    <Switch
                      isChecked={weeklyRecap}
                      onChange={async (e) => {
                        const val = e.target.checked;
                        setWeeklyRecap(val);
                        try {
                          await updatePool(SEASON_ID, poolId, { weekly_recap_enabled: val });
                          toast({ title: val ? "Weekly recaps enabled." : "Weekly recaps disabled.", status: "success", duration: 2000, isClosable: true });
                        } catch {
                          setWeeklyRecap(!val);
                          toast({ title: "Failed to update recap setting.", status: "error", duration: 3000, isClosable: true });
                        }
                      }}
                      colorScheme="blue"
                      isDisabled={!isOwner}
                    />
                  </HStack>

                  <HStack justify="space-between">
                    <Box>
                      <FormLabel fontSize="sm" mb={0}>Pick Reminder Emails</FormLabel>
                      <Text fontSize="xs" color="gray.500">Remind members who haven't picked, 2 days and 1 day before kickoff</Text>
                    </Box>
                    <Switch
                      isChecked={pickReminders}
                      onChange={async (e) => {
                        const val = e.target.checked;
                        setPickReminders(val);
                        try {
                          await updatePool(SEASON_ID, poolId, { pick_reminders_enabled: val });
                          toast({ title: val ? "Pick reminders enabled." : "Pick reminders disabled.", status: "success", duration: 2000, isClosable: true });
                        } catch {
                          setPickReminders(!val);
                          toast({ title: "Failed to update reminder setting.", status: "error", duration: 3000, isClosable: true });
                        }
                      }}
                      colorScheme="blue"
                      isDisabled={!isOwner}
                    />
                  </HStack>

                  {isOwner && (
                    <Flex w="100%" gap={2}>
                      <Button flex="1" size={{ base: "sm", md: "md" }} bg={TOP_BAR_BLUE} color="white" _hover={{ bg: "#00004a" }} isLoading={saving} onClick={handleSave}>
                        Save<Box as="span" display={{ base: "none", sm: "inline" }}> Changes</Box>
                      </Button>
                      <Button
                        flex="1"
                        size={{ base: "sm", md: "md" }}
                        variant="outline"
                        borderColor={TOP_BAR_BLUE}
                        color={TOP_BAR_BLUE}
                        _hover={{ bg: "gray.50" }}
                        onClick={() => navigate(`/premier-league/current/${poolId}`)}
                      >
                        Open Pool
                      </Button>
                      <Button
                        flex="1"
                        size={{ base: "sm", md: "md" }}
                        variant="outline"
                        borderColor="orange.400"
                        color="orange.500"
                        _hover={{ bg: "orange.50" }}
                        isLoading={sendingRecap}
                        onClick={async () => {
                          setSendingRecap(true);
                          try {
                            await sendPlRecapTest(SEASON_ID, poolId);
                            toast({ title: "Test recap sent to your email.", status: "success", duration: 3000, isClosable: true });
                          } catch (err) {
                            toast({ title: err?.response?.data?.detail || "Failed to send test recap.", status: "error", duration: 4000, isClosable: true });
                          } finally {
                            setSendingRecap(false);
                          }
                        }}
                      >
                        <Box as="span" display={{ base: "none", sm: "inline" }}>Send </Box>Test Recap
                      </Button>
                    </Flex>
                  )}
                </VStack>
              </Box>

              {/* Payouts */}
              <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={8} h="100%">
                <VStack align="stretch" spacing={5}>
                  <HStack justify="space-between" align="baseline">
                    <Heading size="md" color={TOP_BAR_BLUE}>Payouts</Heading>
                    {pool?.total_pot != null && (
                      <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                        Total Pot: <Text as="span" color={TOP_BAR_BLUE} fontWeight="bold">${Number(pool.total_pot).toFixed(2)}</Text>
                      </Text>
                    )}
                  </HStack>

                  {payoutError && <Alert status="error" borderRadius="md"><AlertIcon />{payoutError}</Alert>}

                  <VStack align="stretch" spacing={2}>
                    {payouts.map((row, i) => (
                      <HStack key={i} spacing={3}>
                        <Input
                          value={row.label}
                          onChange={(e) => { const next = [...payouts]; next[i] = { ...next[i], label: e.target.value }; setPayouts(next); }}
                          placeholder="Label (e.g. 1st Place)"
                          size="sm"
                          flex="1"
                        />
                        <Input
                          value={row.amount}
                          onChange={(e) => { const next = [...payouts]; next[i] = { ...next[i], amount: e.target.value }; setPayouts(next); }}
                          placeholder="$0"
                          size="sm"
                          w="100px"
                          type="number"
                          min={0}
                        />
                        <IconButton
                          icon={<DeleteIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          aria-label="Remove row"
                          onClick={() => setPayouts(payouts.filter((_, j) => j !== i))}
                        />
                      </HStack>
                    ))}
                  </VStack>

                  {payouts.length > 0 && (
                    <PayoutTotalLine
                      amounts={payouts.map((r) => r.amount)}
                      pot={pool?.total_pot}
                    />
                  )}

                  {isAdmin && (
                    <HStack>
                      <Button
                        size="sm"
                        variant="outline"
                        borderColor={TOP_BAR_BLUE}
                        color={TOP_BAR_BLUE}
                        onClick={() => setPayouts([...payouts, { label: "", amount: "" }])}
                      >
                        + Add Row
                      </Button>
                      <Button
                        size="sm"
                        bg={TOP_BAR_BLUE}
                        color="white"
                        _hover={{ bg: "#00004a" }}
                        isLoading={savingPayouts}
                        onClick={handleSavePayouts}
                      >
                        Save Payouts
                      </Button>
                    </HStack>
                  )}
                </VStack>
              </Box>
            </SimpleGrid>

            {/* Members */}
            <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={8}>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                  <Heading size="md" color={TOP_BAR_BLUE}>Members</Heading>
                  <HStack spacing={3} flexWrap="wrap">
                    {(() => {
                      const members = pool.members || [];
                      const paidCount = members.filter((m) => m.paid).length;
                      const depth = pool.pool_depth;
                      const totalEntries = members.reduce((sum, m) => sum + (m.entry_count || 0), 0);
                      const paidEntries = members.filter((m) => m.paid).reduce((sum, m) => sum + (m.entry_count || 0), 0);
                      return (
                        <>
                          <Text fontSize="sm" color="gray.500">{pool.user_count} joined · {totalEntries} entries</Text>
                          <Badge colorScheme={paidCount === members.length && members.length > 0 ? "green" : "orange"} fontSize="sm" px={2}>
                            {paidCount} / {members.length} paid
                          </Badge>
                          {depth && (
                            <Text fontSize="sm" color="gray.500">
                              ${paidEntries * depth} / ${totalEntries * depth} collected
                            </Text>
                          )}
                        </>
                      );
                    })()}
                  </HStack>
                </HStack>

                {pool.members && pool.members.length > 0 ? (
                  <Box overflowX="auto" overflowY="auto" maxH="400px">
                    <Table size="sm" variant="simple">
                      <Thead position="sticky" top={0} bg="white" zIndex={1}>
                        <Tr>
                          <Th>Member</Th>
                          <Th>Email</Th>
                          <Th textAlign="center">Paid</Th>
                          <Th textAlign="center">Co-Admin</Th>
                          {isOwner && <Th />}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {pool.members.map((m) => (
                          <Tr key={m.user_id}>
                            <Td>
                              <HStack spacing={2}>
                                <Circle size="28px" bg={TOP_BAR_BLUE} color="white" fontWeight="bold" fontSize="xs" flexShrink={0}>
                                  {(m.username || "?").charAt(0).toUpperCase()}
                                </Circle>
                                <Text fontWeight="semibold" fontSize="sm">{m.username || "—"}</Text>
                                {m.user_id === pool.owner_user_id && <Badge colorScheme="blue" fontSize="2xs">Admin</Badge>}
                              </HStack>
                            </Td>
                            <Td>
                              <Text fontSize="sm" color="gray.600" noOfLines={1}>{m.email || "—"}</Text>
                            </Td>
                            <Td textAlign="center">
                              <Checkbox
                                isChecked={!!m.paid}
                                isDisabled={!isAdmin}
                                colorScheme="green"
                                onChange={(e) => handleTogglePaid(m.user_id, e.target.checked)}
                              />
                            </Td>
                            <Td textAlign="center">
                              {m.user_id !== pool.owner_user_id ? (
                                isOwner ? (
                                  <Checkbox
                                    isChecked={!!m.is_co_admin}
                                    colorScheme="blue"
                                    onChange={(e) => handleToggleCoAdmin(m.user_id, e.target.checked)}
                                  />
                                ) : m.is_co_admin ? (
                                  <Badge colorScheme="blue" fontSize="2xs">Co-Admin</Badge>
                                ) : null
                              ) : null}
                            </Td>
                            {isOwner && (
                              <Td textAlign="right" px={1}>
                                {m.user_id !== user.id && (
                                  <Tooltip label="Remove member" hasArrow>
                                    <IconButton
                                      icon={<DeleteIcon />}
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="red"
                                      aria-label="Remove member"
                                      onClick={() => handleRemoveMember(m.user_id)}
                                    />
                                  </Tooltip>
                                )}
                              </Td>
                            )}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                ) : (
                  <Text fontSize="sm" color="gray.500">No members yet.</Text>
                )}
              </VStack>
            </Box>

            {/* Invite/Message Players */}
            {isAdmin && (
              <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={8}>
                <VStack align="stretch" spacing={4}>
                  <HStack justify="space-between" align="center">
                    <Heading size="md" color={TOP_BAR_BLUE}>Invite/Message Players</Heading>
                    <Button
                      size="xs"
                      minH="44px"
                      variant="outline"
                      borderColor={TOP_BAR_BLUE}
                      color={TOP_BAR_BLUE}
                      _hover={{ bg: "gray.50" }}
                      onClick={addAllMembers}
                    >
                      Add all members
                    </Button>
                  </HStack>

                  {/* Mode toggle */}
                  <HStack spacing={0} borderRadius="md" overflow="hidden" border={`1.5px solid ${invitesClosed ? "#b0b0b0" : TOP_BAR_BLUE}`} alignSelf="flex-start">
                    <Button
                      size="sm"
                      minH="44px"
                      borderRadius="0"
                      bg={sendMode === "invite" ? TOP_BAR_BLUE : "white"}
                      color={sendMode === "invite" ? "white" : invitesClosed ? "gray.400" : TOP_BAR_BLUE}
                      _hover={{ bg: invitesClosed ? "white" : sendMode === "invite" ? TOP_BAR_BLUE : "gray.50" }}
                      onClick={invitesClosed ? undefined : switchToInvite}
                      isLoading={sendMode === "invite" && linkLoading}
                      isDisabled={invitesClosed}
                      cursor={invitesClosed ? "not-allowed" : "pointer"}
                      title={invitesClosed ? "Invites are closed — season has started" : undefined}
                      px={5}
                    >
                      Invite
                    </Button>
                    <Button
                      size="sm"
                      minH="44px"
                      borderRadius="0"
                      bg={sendMode === "message" ? TOP_BAR_BLUE : "white"}
                      color={sendMode === "message" ? "white" : TOP_BAR_BLUE}
                      _hover={{ bg: sendMode === "message" ? TOP_BAR_BLUE : "gray.50" }}
                      onClick={switchToMessage}
                      px={5}
                    >
                      Message
                    </Button>
                  </HStack>

                  {/* Invite link display */}
                  {sendMode === "invite" && inviteLink && (
                    <HStack bg="gray.50" px={3} py={2} borderRadius="md" border="1px solid #e2e8f0">
                      <Text fontSize="xs" color="gray.600" flex="1" isTruncated>{inviteLink}</Text>
                      <Button size="xs" minH="44px" variant="outline" borderColor={TOP_BAR_BLUE} color={TOP_BAR_BLUE} onClick={handleCopyLink} flexShrink={0}>
                        Copy Link
                      </Button>
                    </HStack>
                  )}

                  <FormControl>
                    <FormLabel fontSize="sm">Email Addresses</FormLabel>
                    <HStack>
                      <Input
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyDown={handleEmailKeyDown}
                        placeholder="Enter email and press Enter"
                        size="sm"
                      />
                      <Button size="sm" minH="44px" onClick={addEmail} bg={TOP_BAR_BLUE} color="white" _hover={{ bg: "#00004a" }} flexShrink={0}>
                        Add
                      </Button>
                    </HStack>
                    {emails.length > 0 && (
                      <Flex mt={2} flexWrap="wrap" gap={2}>
                        {emails.map((addr) => (
                          <Tag key={addr} size="sm" borderRadius="full" variant="solid" colorScheme="blue">
                            <TagLabel>{addr}</TagLabel>
                            <TagCloseButton onClick={() => removeEmail(addr)} />
                          </Tag>
                        ))}
                      </Flex>
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Message</FormLabel>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={sendMode === "message" ? "Add message here..." : ""}
                      rows={7}
                      fontSize="sm"
                    />
                  </FormControl>

                  <Button
                    bg={TOP_BAR_BLUE}
                    color="white"
                    _hover={{ bg: "#00004a" }}
                    isLoading={sending}
                    onClick={handleSend}
                    alignSelf="flex-start"
                  >
                    {sendMode === "invite"
                      ? `Send Invite${emails.length > 1 ? "s" : ""}`
                      : `Send Message${emails.length > 1 ? "s" : ""}`}
                  </Button>
                </VStack>
              </Box>
            )}

          </VStack>
        )}
      </Container>
    </Box>
  );
}
